import type { Money, TradeIdea } from '../../api/client'
import { betrag } from '../../pages/priceFormat'
import { leiterAus, zahl } from '../../hooks/ideenRegeln'
import s from './Kursleiter.module.scss'

/** Höhe einer Fahnenzeile und der Achse, in Pixeln. */
const ZEILE = 26
/** Mit Umrechnung trägt die Fahne drei Zeilen — die Zeile wird höher. */
const ZEILE_UMGERECHNET = 40
const ACHSE = 10

/**
 * Die Kursleiter — die Schwellen einer Idee auf einer Preisachse.
 *
 * Links ist billiger, rechts teurer. Schraffiert, wo die Idee widerlegt ist;
 * grün die Einstiegszone; gelb, wo noch gekauft wird, aber nicht mehr
 * nachgelaufen. Der Kurs ist die eine Marke, die sich bewegt. Klein in der
 * Zeile, groß im aufgeklappten Eintrag — dann mit Referenz, Schnitt und
 * Zielen. Die Achse bleibt in der Handelswährung; mit Zielwährung steht an
 * jeder Fahne der umgerechnete Betrag über dem gehandelten.
 */
export function Kursleiter({ idee, ziele = [], gross = false, umrechnung }: {
  idee: TradeIdea
  /** Die Kursziele — kommen erst mit dem Detail. */
  ziele?: Money[]
  /** Zwei Fahnenzeilen je Seite statt einer, alle Marken beschriftet. */
  gross?: boolean
  /** Die Zielwährung aus der Kopfzeile — leer heißt: keine. */
  umrechnung?: string
}) {
  const leiter = leiterAus(idee, ziele, gross, umrechnung)
  if (!leiter) {
    const referenz = zahl(idee.reference_price)
    const kurs = zahl(idee.last_price)
    return (
      <div className={s.ohneZone}>
        Ohne Zone: nur beobachten.
        {referenz !== null && ` Referenz ${betrag(String(referenz))}`}
        {kurs !== null && ` · Kurs ${betrag(String(kurs))} ${idee.currency}`}
      </div>
    )
  }

  const zeile = leiter.marken.some((m) => m.original) ? ZEILE_UMGERECHNET : ZEILE
  const achseOben = leiter.zeilenOben * zeile + 6
  const hoehe = achseOben + ACHSE + 6 + leiter.zeilenUnten * zeile

  return (
    <div className={s.leiter} style={{ height: hoehe }} data-tot={leiter.tot || undefined}
         role="img" aria-label="Kursleiter: die Schwellen der Idee auf einer Preisachse">
      <div className={s.basis} style={{ top: achseOben + ACHSE / 2 }} />
      {leiter.segmente.map((seg) => (
        <div key={seg.key} className={`${s.segment} ${s[seg.art]}`}
             style={{ left: `${seg.links}%`, width: `${seg.breite}%`, top: achseOben, height: ACHSE }} />
      ))}
      {leiter.marken.map((m) => {
        const rand = m.x < 4 ? 'links' : m.x > 96 ? 'rechts' : undefined
        const oben = m.seite === 'oben'
        const fahneTop = m.seite === null ? 0
          : oben ? (leiter.zeilenOben - 1 - m.zeile) * zeile
                 : achseOben + ACHSE + 6 + m.zeile * zeile
        return (
          <div key={m.key} className={s.marke} data-ton={m.ton}>
            <div className={s.strich} data-fett={m.fett || undefined}
                 style={{ left: `${m.x}%`, top: achseOben - (m.fett ? 6 : 3),
                          height: ACHSE + (m.fett ? 12 : 6) }} />
            {m.seite !== null && (
              <div className={s.fahne} data-rand={rand} style={{ left: `${m.x}%`, top: fahneTop }}>
                <div className={s.wert} data-fett={m.fett || undefined}>{m.wert}</div>
                {m.original && <div className={s.original}>{m.original}</div>}
                <div className={s.label} data-fett={m.fett || undefined}>{m.label}</div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
